import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/base/ScrollToTop';
import { movies, categories, categoryDetails } from '@/mocks/movies';

type SortKey = 'rating' | 'year' | 'update';

const heroImages: Record<string, string> = {
  all: "https://readdy.ai/api/search-image?query=Dark%20cinematic%20movie%20collection%20backdrop%2C%20film%20strips%20and%20projector%20light%2C%20warm%20golden%20and%20amber%20tones%2C%20elegant%20theater%20atmosphere%2C%20luxurious%20dark%20background%2C%20premium%20cinema%20aesthetic%2C%20rich%20textures&width=1920&height=600&seq=category-hero-all&orientation=landscape",
  movies: "https://readdy.ai/api/search-image?query=Dark%20cinematic%20movie%20collection%20backdrop%2C%20film%20strips%20and%20projector%20light%2C%20warm%20golden%20and%20amber%20tones%2C%20elegant%20theater%20atmosphere%2C%20luxurious%20dark%20background%2C%20premium%20cinema%20aesthetic%2C%20rich%20textures&width=1920&height=600&seq=category-hero-movies&orientation=landscape",
  "tv-shows": "https://readdy.ai/api/search-image?query=Dark%20cinematic%20TV%20series%20collection%20backdrop%2C%20television%20screens%20with%20warm%20glow%2C%20golden%20and%20amber%20tones%2C%20elegant%20living%20room%20theater%20atmosphere%2C%20luxurious%20dark%20background%2C%20premium%20entertainment%20aesthetic%2C%20cozy%20cinematic%20lighting&width=1920&height=600&seq=category-hero-tvshows&orientation=landscape",
  anime: "https://readdy.ai/api/search-image?query=Dark%20cinematic%20anime%20collection%20backdrop%2C%20colorful%20illustration%20style%20with%20warm%20lighting%2C%20golden%20and%20amber%20accents%2C%20artistic%20Japanese%20anime%20atmosphere%2C%20luxurious%20dark%20background%2C%20premium%20animation%20aesthetic%2C%20rich%20textures&width=1920&height=600&seq=category-hero-anime&orientation=landscape",
  variety: "https://readdy.ai/api/search-image?query=Dark%20cinematic%20variety%20show%20backdrop%2C%20stage%20spotlights%20with%20warm%20golden%20glow%2C%20entertainment%20atmosphere%2C%20elegant%20theater%20setting%2C%20luxurious%20dark%20background%2C%20premium%20show%20aesthetic%2C%20rich%20dramatic%20lighting&width=1920&height=600&seq=category-hero-variety&orientation=landscape",
};

const metaDescriptions: Record<string, string> = {
  all: '久久影视影视大全，提供高清电影、电视剧、动漫、综艺免费在线观看，海量片源画质清晰播放流畅，零成本畅享视听盛宴。',
  movies: '久久影视电影频道，高清电影免费在线观看，海量优质电影资源每日更新，画质清晰不缓冲，畅享极致观影体验。',
  'tv-shows': '久久影视电视剧频道，高清电视剧免费在线观看，海量优质剧集资源每日更新，画质清晰不缓冲，畅享追剧体验。',
  anime: '久久影视动漫频道，高清动漫免费在线观看，海量优质动漫资源每日更新，画质清晰不缓冲，畅享二次元世界。',
  variety: '久久影视综艺频道，高清综艺免费在线观看，海量优质综艺资源每日更新，画质清晰不缓冲，畅享欢乐时光。',
};

const metaKeywords: Record<string, string> = {
  all: '高清影视,免费在线观看,久久影视,影视大全,电影电视剧,动漫综艺',
  movies: '高清电影,免费电影,在线观看,久久影视,电影大全,最新电影,热门电影',
  'tv-shows': '高清电视剧,免费电视剧,在线追剧,久久影视,电视剧大全,热播剧,最新剧集',
  anime: '高清动漫,免费动漫,在线看动漫,久久影视,动漫大全,日本动漫,国产动漫',
  variety: '高清综艺,免费综艺,在线看综艺,久久影视,综艺大全,热门综艺,真人秀',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<SortKey>('rating');
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categoryDetails[activeCategory] || categoryDetails.all;

  const categoryMovies = activeCategory === 'all'
    ? movies
    : movies.filter(m => m.categorySlug === activeCategory);

  const sortedMovies = [...categoryMovies].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'year') return b.year - a.year;
    return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
  });

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory.name },
  ];

  const heroImage = heroImages[activeCategory] || heroImages.all;
  const metaDesc = metaDescriptions[activeCategory] || metaDescriptions.all;
  const metaKw = metaKeywords[activeCategory] || metaKeywords.all;

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="CollectionPage"
        data={{
          name: currentCategory.name + ' - 久久影视',
          description: metaDesc,
          url: 'https://www.jiujiutv.com/category/' + activeCategory,
          isPartOf: { '@type': 'WebSite', name: '久久影视', url: 'https://www.jiujiutv.com' },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jiujiutv.com/' },
            { '@type': 'ListItem', position: 2, name: currentCategory.name, item: 'https://www.jiujiutv.com/category/' + activeCategory },
          ],
        }}
      />

      <Navbar />

      <main>
        <section className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
          <img
            src={heroImage}
            alt={currentCategory.name}
            title={'久久影视 ' + currentCategory.name + ' - 高清' + currentCategory.name + '免费在线观看'}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/20">
                <i className={currentCategory.icon + ' text-primary-500 text-2xl'}></i>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-400 max-w-xl mx-auto leading-relaxed">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </section>

        <section className="py-6 md:py-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-2 flex-wrap">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={'/category/' + cat.slug}
                      className={(activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-950'
                        : 'bg-background-200 text-foreground-400 hover:text-foreground-50') + ' px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap'}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                  {[
                    { key: 'rating' as SortKey, label: '评分最高' },
                    { key: 'year' as SortKey, label: '最新年份' },
                    { key: 'update' as SortKey, label: '最近更新' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={(sortBy === opt.key
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-200 text-foreground-500 hover:text-foreground-50') + ' px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap'}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-xs text-foreground-600">
                  共找到 <strong className="text-primary-500">{sortedMovies.length}</strong> 部{currentCategory.name}资源
                </p>
              </div>

              {sortedMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {sortedMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                    <i className="ri-inbox-line text-foreground-500 text-3xl"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无内容，请浏览其他分类</p>
                  <Link
                    to="/category/all"
                    className="inline-flex items-center gap-2 mt-4 px-6 py-2 rounded-full bg-primary-500 text-background-950 text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                  >
                    查看全部影视
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}